import { Children, isValidElement, useMemo, useState } from "react";
import { bool, string, object, node, arrayOf, func, oneOfType, shape } from "prop-types";
import { DragDropProvider } from "@dnd-kit/react";
import { arrayMove } from "@dnd-kit/helpers";
import { DragDropManager } from "@dnd-kit/dom";
import { DragDropManagerModifierSetup, DragHandle } from "@components";
import { SortableContainer, SortableItem } from "./components";
import { DragDropManagerPluginSetup, DragDropManagerSensorSetup } from "@components/SortableListV2/SortableListV2Config";

export const SortableListV2 = ({
  group,
  list = [],
  onListReorder,
  onDragStart,
  onDragEnd,
  onDragCancel,
  disabled,
  children,
  dragHandleProps,
  itemContainerProps,
  childIdKey,
  itemIdKey="id",
  useDragHandle = false,
  horizontal = false,
  lockAxis = false,
  // useOverlay = false,
  restrictToWindow = false,
  ...containerProps
}) => {
  const items = list?.map((item) => ({ id: item[itemIdKey] }));
  
  const [containerRef, setContainerRef] = useState(null);
  
  const manager = useMemo(() => (
    new DragDropManager({
      plugins: DragDropManagerPluginSetup,
      sensors: DragDropManagerSensorSetup,
      modifiers: DragDropManagerModifierSetup(lockAxis, horizontal),
    })
  ), []);
  
  const handleDragStart = (event) => {
    const { operation } = event;
    if (!operation.source.id)
      return;
    
    onDragStart?.(event);
  }
  
  const handleDragEnd = (event) => {
    const { operation, canceled } = event;
    
    if (canceled) {
      onDragCancel?.(event);
      return;
    }
    
    if(operation.source.initialIndex !== operation.source.index)
      onListReorder(arrayMove(items, operation.source.initialIndex, operation.source.index));
    
    onDragEnd?.(event);
  }
  
  const childrenWithProps = Children.map(children, (child, index) => {
    if ( isValidElement(child) && child.props[childIdKey || itemIdKey])
      return <SortableItem
        {...itemContainerProps}
        key={ child.props[childIdKey || itemIdKey] }
        id={ child.props[childIdKey || itemIdKey] }
        index={index}
        // overlay={!useOverlay}
        dragHandleProps={dragHandleProps}
        useDragHandle={useDragHandle}
        disabled={disabled}
        restrictToWindow={restrictToWindow}
        containerElement={containerRef}
      >
        { child }
      </SortableItem>
    return child;
  });
  
  return (
    <DragDropProvider
      manager={manager}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContainer
        id={group}
        ref={setContainerRef}
        disabled={disabled}
        { ...containerProps }
      >
        { childrenWithProps }
      </SortableContainer>
      {/*{*/}
      {/*  useOverlay &&*/}
      {/*  <Portal>*/}
      {/*    <DragOverlay>*/}
      {/*      {*/}
      {/*        typeof activeIndex === "number" && children[activeIndex]*/}
      {/*      }*/}
      {/*    </DragOverlay>*/}
      {/*  </Portal>*/}
      {/*}*/}
    </DragDropProvider>
  )
};

SortableListV2.propTypes = {
  list: arrayOf(object).isRequired,
  group: string.isRequired,
  children: oneOfType([string, object, node]).isRequired,
  itemIdKey: string,
  childIdKey: string,
  onListReorder: func,
  onDragStart: func,
  onDragEnd: func,
  onDragCancel: func,
  disabled: bool,
  className: string,
  style: object,
  useDragHandle: bool,
  // useOverlay: bool,
  horizontal: bool,
  lockAxis: bool,
  restrictToWindow: bool,
  dragHandleProps: shape(DragHandle.propTypes),
  itemContainerProps: shape({
    useDragHandle: bool,
    dragHandleProps: shape(DragHandle.propTypes),
    className: string,
    style: object,
    wrap: string,
    alignItems: string,
    justifyContent: string,
    restrictToWindow: bool,
  }),
};
